/**
 * Contact Form Schema
 *
 * Single source of truth for contact form validation.
 * Migrated from @designs-by-maryam/types for centralized validation.
 */
import { z } from "zod";
import { PROJECT_TYPE_OPTIONS, projectTypeSchema } from "../enums/project";
import { sanitizeAndTrim } from "../primitives/sanitize";
import { bahrainPhoneRegex, bahrainPhoneSchema } from "../primitives/strings";

// Re-export phone regex for backwards compatibility
export { bahrainPhoneRegex };

/** Contact form submission schema with XSS sanitization */
export const contactFormSchema = z.object({
	name: z
		.string()
		.min(2, "Name must be at least 2 characters")
		.transform(sanitizeAndTrim),
	email: z.string().email("Please enter a valid email address"),
	phone: bahrainPhoneSchema,
	projectType: projectTypeSchema,
	budgetRange: z.union([z.string(), z.undefined()]),
	timeline: z.union([z.string(), z.undefined()]),
	details: z
		.string()
		.min(10, "Please provide at least 10 characters about your project")
		.transform(sanitizeAndTrim),
});
export type ContactFormData = z.infer<typeof contactFormSchema>;

/** Budget range options for form dropdowns */
export const BUDGET_RANGE_OPTIONS = [
	{ value: "under-5k", label: "Under 5,000 BHD" },
	{ value: "5k-10k", label: "5,000 - 10,000 BHD" },
	{ value: "10k-25k", label: "10,000 - 25,000 BHD" },
	{ value: "25k-50k", label: "25,000 - 50,000 BHD" },
	{ value: "over-50k", label: "Over 50,000 BHD" },
] as const;

/** Timeline options for form dropdowns */
export const TIMELINE_OPTIONS = [
	{ value: "asap", label: "As soon as possible" },
	{ value: "1-3-months", label: "1-3 months" },
	{ value: "3-6-months", label: "3-6 months" },
	{ value: "6-12-months", label: "6-12 months" },
	{ value: "flexible", label: "Flexible" },
] as const;

// Re-export project type options for form dropdowns
export { PROJECT_TYPE_OPTIONS };

// ============================================
// OUTPUT SCHEMAS
// ============================================

/** Contact form submission response */
export const contactSubmitOutput = z.object({
	success: z.boolean(),
	message: z.string(),
	submissionId: z.string().uuid().optional(),
});
export type ContactSubmitOutput = z.infer<typeof contactSubmitOutput>;
